<script setup lang="ts">
import { computed, h, onMounted, ref } from 'vue'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import { formatNumberSemiTruncate } from '@/helpers/utils'
import { usePlayer } from '@/composables/game/player'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import type { Exchange, ExchangeItem, ExchangeItemReward } from '@/types/shared/exchange'
import { EXCHANGE_ITEM_COST_TYPE } from '@/types/shared/exchange'
import { useWatchOverlays } from '@/composables/helpers/scoll'
import { useExchange } from '@/composables/game/exchange'
import { useConfig } from '@/composables/game/config'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import api from '@/services/api'
import { useToaster } from '@/composables/game/toasters'
import { allSpecialCurrenciesConfig } from '@/config/special-currencies'
import { useGiveaway } from '@/composables/game/giveaway'
import ExchangeItemComponent from '@/components/exchange/ExchangeItem.vue'
import ExchangePurchaseModal from '@/components/exchange/ExchangePurchaseModal.vue'
import ExchangeConfirmModal from '@/components/exchange/ExchangeConfirmModal.vue'
import ExplodeEmojisAnimation from '@/components/ExplodeEmojisAnimation.vue'
import OutOfSpecialCurrencyModal from '@/components/modals/OutOfSpecialCurrencyModal.vue'
import { useOutOfSpecialCurrency, usePathwayEventOverlay, usePathwayEvents } from '@/composables/game/pathway-events'
import { useRouter } from 'vue-router'
import { RouteName } from '@/routes'
import { useConfetti } from '@/composables/game/confetti'
import CrossCircleIcon from '@/components/icons/CrossCircleIcon.vue'
import { useBackPack } from '@/composables/game/backpack'
import { formatDuration } from '@/helpers/date'
import ExchangeMainIcon from '@/assets/images/exchange/exchange-main.webp'
import { rewardImageMap } from '@/composables/game/exchange-rewards'

const props = defineProps<{
  show: boolean
  exchange: Exchange
}>()

const emit = defineEmits<{
  (e: 'hide'): void
}>()

const { topNumber } = useFullScreenDimensions()
const { coins, updatePlayer, player, goldenCoinValue } = usePlayer()
const { syncConfig } = useConfig()
const { availableSpins, updateState: updateFortuneWheelState } = useFortuneWheel()
const { countdownForExchangeEnd, isCountdownCritical } = useExchange()
const { addHapticFeedback } = useHapticFeedback()
const { notify } = useToaster()
const { playerTickets, syncGiveaway } = useGiveaway()
const { showConfetti } = useConfetti()
const { showBackPackOverlay } = useBackPack()
const {
  selectedSpecialCurrencyForModal,
  canShowOutOfSpecialCurrencyModal,
  getAvailableEcommerceItems,
  closeOutOfSpecialCurrencyModal,
} = useOutOfSpecialCurrency()
const router = useRouter()
const { anyActivePathwayEvent, pathwayEvents } = usePathwayEvents()
const { showFirstPathway } = usePathwayEventOverlay()

const scale = ref(1)
const itemsContainer = ref<HTMLElement | null>(null)
const selectedItem = ref<{ item: ExchangeItem, itemId: string } | null>(null)
const showPurchaseModal = ref(false)
const showConfirmModal = ref(false)
const selectedConfirmItem = ref<{ item: ExchangeItem, itemId: string } | null>(null)
const loadingItemId = ref<null | string>(null)
const isProcessingPurchase = computed(() => loadingItemId.value !== null)

const nonReactiveCoins = ref(coins.value)

const goldCircleAnimationItems = ref<Record<string, boolean>>({
  goldenCoins: false,
  spins: false,
  dropeeStars: false,
  giveawayTickets: false,
  ...Object.fromEntries(
    Object.keys(allSpecialCurrenciesConfig.value).map(id => [id, false]),
  ),
})

const lastWonRewardImage = ref<string>('')

// Get keys of currencies displayed in balance pills
const displayedCurrencyKeys = computed(() => mostFrequentCosts.value.map(cost => getResourceKey(cost)))

// Find which reward keys are currently active (triggered by showGoldenCircles)
const activeRewardKeys = computed(() =>
  Object.entries(goldCircleAnimationItems.value)
    .filter(([_, isActive]) => isActive)
    .map(([key]) => key)
)

// Only show backpack animation if there's an active reward that is NOT displayed in balance pills
const showBackpackAnimation = computed(() => {
  return activeRewardKeys.value.some(key => !displayedCurrencyKeys.value.includes(key))
})

// Only bounce if there's an active reward that is NOT displayed in balance pills
const backpackBounce = computed(() => {
  return activeRewardKeys.value.some(key => !displayedCurrencyKeys.value.includes(key))
})

useWatchOverlays(computed(() => props.show))

const sortedItems = computed(() => {
  return Object.entries(props.exchange.config.items).map(([id, item]) => ({
    id,
    ...item,
  }))
})

const balanceSummary = computed<Record<string, number>>(() => {
  return {
    spins: availableSpins.value,
    goldenCoins: nonReactiveCoins.value,
    giveawayTickets: playerTickets.value,
    dropeeStars: player.value.ecommerce.starsBalance,
    ...Object.fromEntries(
      Object.keys(allSpecialCurrenciesConfig.value).map(id => [
        id,
        (player.value.specialCurrencies as Record<string, number>)[id] ?? 0,
      ]),
    ),
  }
})

function getResourceKey(cost: ExchangeItem['cost']): string {
  if (cost.type === EXCHANGE_ITEM_COST_TYPE.FREE) {
    return ''
  }
  if (cost.type === EXCHANGE_ITEM_COST_TYPE.STAR) {
    return 'dropeeStars'
  }
  return cost.currencyId || 'goldenCoins'
}

const mostFrequentCosts = computed(() => {
  const allCosts = Object.values(props.exchange.config.items)
    .map(item => item.cost)
    .filter(cost => cost && cost.type !== EXCHANGE_ITEM_COST_TYPE.FREE)

  const costMap = new Map()
  for (const cost of allCosts) {
    const key = cost.type === EXCHANGE_ITEM_COST_TYPE.SPECIAL_CURRENCY ? `${cost.type}:${cost.currencyId}` : cost.type
    if (costMap.has(key)) {
      costMap.get(key).count++
    } else {
      costMap.set(key, { cost, count: 1 })
    }
  }

  return Array.from(costMap.values())
    .sort((a, b) => b.count - a.count)
    .map(entry => entry.cost)
})

function closePurchaseModal() {
  showPurchaseModal.value = false
  setTimeout(() => selectedItem.value = null, 100)
}

function onPayForItem(itemId: string) {
  if (isProcessingPurchase.value) return

  const item = sortedItems.value.find(i => i.id === itemId)
  if (!item) return

  const hasBalance = checkItemBalance(item)
  if (!hasBalance && item.cost?.type === EXCHANGE_ITEM_COST_TYPE.SPECIAL_CURRENCY) {
    onOutOfSpecialCurrency(item.cost.currencyId)
    return
  }

  selectedConfirmItem.value = { item, itemId }
  showConfirmModal.value = true
}

async function onConfirmPay() {
  if (!selectedConfirmItem.value || isProcessingPurchase.value) return

  const { item, itemId } = selectedConfirmItem.value
  loadingItemId.value = itemId

  try {
    await api.payForExchangeItem({ itemId })
    closeConfirmModal()
    showGoldenCircles(item.reward)
    await showConfetti({ ...getConfettiShapes(item) })
    await onSuccessfulPurchase()
  } catch (e) {
    notify({ type: 'error', title: 'Failed to purchase' })
  } finally {
    loadingItemId.value = null
  }
}

function closeConfirmModal() {
  showConfirmModal.value = false
  setTimeout(() => selectedConfirmItem.value = null, 100)
}

function checkItemBalance(item: ExchangeItem & { id: string }): boolean {
  if (!item.cost) return true

  switch (item.cost.type) {
  case EXCHANGE_ITEM_COST_TYPE.FREE:
    return true
  case EXCHANGE_ITEM_COST_TYPE.STAR:
    return player.value.ecommerce.starsBalance >= (item.cost.amount || 0)
  case EXCHANGE_ITEM_COST_TYPE.SPECIAL_CURRENCY:
    return (player.value.specialCurrencies[item.cost.currencyId] || 0) >= (item.cost.amount || 0)
  default:
    return false
  }
}

function onOutOfSpecialCurrency(currencyId: string) {
  if (canShowOutOfSpecialCurrencyModal(currencyId)) {
    selectedSpecialCurrencyForModal.value = currencyId
    return
  }
  notify({
    type: 'error',
    title: `Not enough <img width="20px" height="20px" style="display: inline-block; vertical-align: middle;" src="${rewardImageMap.value[currencyId]}" alt="Icon">`,
    description: h('span', { class: 'font-medium' }, [
      h('span', {
        class: 'underline text-accent1-default',
        onClick: () => {
          if (anyActivePathwayEvent.value) {
            showFirstPathway(pathwayEvents.value)
          } else {
            router.push({ name: RouteName.HOME })
          }
          emit('hide')
        },
      }, 'Explore pathways'),
      ' to get more now!',
    ]),
  })
}

function onItemPurchase(item: ExchangeItem, itemId: string) {
  if (isProcessingPurchase.value) return

  selectedItem.value = { item, itemId }
  setTimeout(() => showPurchaseModal.value = true, 100)
}

function getConfettiImages(item: ExchangeItem) {
  const images: { src: string, width: number, height: number }[] = []

  Object.keys(item.reward).forEach(rewardType => {
    let imageKey = ''
    const isSpecialCurrency = rewardType === 'specialCurrencies'
    if (isSpecialCurrency) {
      const specialCurrencies = item.reward.specialCurrencies
      if (specialCurrencies) {
        imageKey = Object.keys(specialCurrencies).find(key => (specialCurrencies as Record<string, number>)[key]) ?? ''
      }
    } else {
      imageKey = rewardType
    }
    const imageSrc = rewardImageMap.value[imageKey]
    if (imageSrc) {
      images.push({ src: imageSrc, width: 32, height: 32 })
    }
  })
  return images
}

function getConfettiShapes(item: ExchangeItem) {
  return {
    count: 150,
    spread: 100,
    startVelocity: 45,
    decay: 0.9,
    shapes: ['image'],
    shapeOptions: {
      image: getConfettiImages(item),
    },
  }
}

function showGoldenCircles(reward: Partial<ExchangeItemReward>) {
  Object.keys(goldCircleAnimationItems.value).forEach(key => {
    goldCircleAnimationItems.value[key as keyof typeof goldCircleAnimationItems.value] = false
  })

  setTimeout(() => {
    Object.keys(reward).forEach(rewardType => {
      let key = ''
      const isSpecialCurrency = rewardType === 'specialCurrencies'
      if (isSpecialCurrency) {
        const specialCurrencies = reward.specialCurrencies as Record<string, number>
        if (specialCurrencies) {
          key = Object.keys(specialCurrencies).find(key => specialCurrencies[key]) ?? ''
        }
      } else {
        key = rewardType
      }
      lastWonRewardImage.value = rewardImageMap.value[key] ?? ''
      goldCircleAnimationItems.value[key] = true
    })
  }, 100)
}

async function onSuccessfulPurchase() {
  addHapticFeedback('medium')
  if (selectedItem.value) {
    showGoldenCircles(selectedItem.value.item.reward)
    await showConfetti({ ...getConfettiShapes(selectedItem.value.item) })
  }
  notify({ type: 'success', title: 'Reward added!' })
  const { playerStats } = await syncConfig()
  updatePlayer(playerStats)
  await updateFortuneWheelState()
  await syncGiveaway()
  if (selectedItem.value?.item.reward.goldenCoins) {
    nonReactiveCoins.value = coins.value
  }
}

function openInventory() {
  emit('hide')
  router.push({ name: RouteName.INVENTORY })
}

onMounted(async () => {
  await syncGiveaway()
})
</script>

<template>
  <Transition
    name="fade"
    mode="out-in"
  >
    <div
      v-if="show"
      class="normal-zeros exchange-overlay-bg fixed bottom-0 left-0 z-[800] flex h-screen max-h-screen w-screen flex-col bg-[url('@/assets/images/app-background.webp')] bg-cover"
      :style="{
        paddingTop: `${topNumber + 24}px`,
      }"
    >
      <div class="z-[4] flex h-full w-full flex-1 flex-col px-4">
        <!-- Header -->
        <div class="z-[1] flex items-center gap-x-4">
          <div class="flex flex-1 flex-wrap items-center gap-2">
            <!-- Vault/Inventory Button -->
            <div class="relative">
              <div
                class="balance-pill scale-in cursor-pointer !p-0"
                :class="[{ 'bounce-up': backpackBounce }]"
                :style="backpackBounce ? { animationDelay: '2.2s' } : {}"
                @click="openInventory()"
              >
                <img
                  src="@/assets/images/inventory.webp"
                  class="size-10"
                  alt="Vault"
                >
              </div>
              <ExplodeEmojisAnimation
                :show="showBackpackAnimation"
                :image-src="lastWonRewardImage"
                :loop="1"
                reverse
              />
            </div>

            <!-- Balance Pills -->
            <div
              v-for="(cost, index) in mostFrequentCosts"
              :key="index"
              class="balance-pill scale-in"
            >
              <template v-if="cost.type !== EXCHANGE_ITEM_COST_TYPE.FREE">
                <div class="relative flex h-5 w-5 min-w-[20px] justify-center">
                  <img
                    class="h-5 w-auto object-cover"
                    :src="rewardImageMap[getResourceKey(cost) as keyof typeof rewardImageMap]"
                  >
                  <ExplodeEmojisAnimation
                    :show="(goldCircleAnimationItems as Record<string, boolean>)[getResourceKey(cost)]"
                    :image-src="undefined"
                    :loop="2"
                  />
                </div>
                <span>{{ formatNumberSemiTruncate(balanceSummary[getResourceKey(cost)]) }}</span>                                
              </template>
            </div>
          </div>
          <CrossCircleIcon
            class="size-8 min-w-[32px] cursor-pointer text-white/[87%]"
            @click="addHapticFeedback('light'); emit('hide')"
          />
        </div>

        <div
          ref="itemsContainer"
          class="flex h-full w-full flex-col overflow-y-auto"
          :style="{
            transform: `scale(${scale})`,
            transformOrigin: 'top center'
          }"
        >
          <div class="relative flex w-full flex-1 flex-col items-center">
            <!-- Main Icon -->
            <img
              :src="ExchangeMainIcon"
              class="pointer-events-none relative -z-[1] size-[120px] object-cover"
              alt="The Exchange"
            >
            <div class="z-[1] text-center text-[36px] leading-[36px] font-bold font-pilat text-white -mt-3">
              THE EXCHANGE
            </div>

            <!-- Timer Banner -->
            <div class="shadow-banner -mt-1.5 -mx-6 w-[calc(100%+48px)] h-[52px] flex items-center justify-center border-y border-y-[#F29C11]/[.66] bg-[#F29C11]/[.66]">
              <div class="uppercase text-white/[.87] text-base font-bold font-pilat text-shadow-default pr-2">
                Ends in
              </div>
              <img
                class="size-[18px] object-cover"
                src="@/assets/images/gold-clock.png"
              >
              <div class="ml-1 golden-text-black-stroke text-base font-bold font-pilat gold-text-shadow pl-1 pr-0.5">
                {{ formatDuration(countdownForExchangeEnd) }}
              </div>
            </div>

            <!-- Items Grid -->
            <div class="mt-7 grid w-full grid-cols-3 gap-x-4 gap-y-5 pb-4">
              <ExchangeItemComponent
                v-for="item in sortedItems"
                :key="item.id"
                :item-id="item.id"
                :item="item"
                @purchase="onItemPurchase(item, item.id)"
                @pay="onPayForItem(item.id)"
                @out-of-special-currency="onOutOfSpecialCurrency"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Purchase Modal (for USD purchases) -->
      <ExchangePurchaseModal
        v-if="selectedItem && showPurchaseModal"
        :show="showPurchaseModal"
        :item="selectedItem.item"
        :item-id="selectedItem.itemId"
        :exchange="exchange"
        @purchase-done="onSuccessfulPurchase"
        @hide="closePurchaseModal"
      />

      <!-- Confirm Modal (for free/currency purchases) -->
      <ExchangeConfirmModal
        v-if="selectedConfirmItem && showConfirmModal"
        :show="showConfirmModal"
        :item="selectedConfirmItem.item"
        :item-id="selectedConfirmItem.itemId"
        :is-loading="loadingItemId === selectedConfirmItem?.itemId"
        @confirm="onConfirmPay"
        @hide="closeConfirmModal"
      />

      <!-- Out of Special Currency Modal -->
      <OutOfSpecialCurrencyModal
        v-if="selectedSpecialCurrencyForModal"
        :show="!!selectedSpecialCurrencyForModal"
        :currency="selectedSpecialCurrencyForModal"
        :ecommerce-items="getAvailableEcommerceItems(selectedSpecialCurrencyForModal)"
        @hide="closeOutOfSpecialCurrencyModal($event)"
      />
    </div>
  </Transition>
</template>

<style scoped lang="scss">
@import '@/assets/styles/sales.scss';

.balance-pill {
	@apply py-1.5 pl-2.5 pr-3 rounded-full bg-dark-top-most-layer flex items-center gap-x-1.5 h-8 text-sm font-semibold -tracking-[0.28px] min-w-max;
}

.exchange-overlay-bg::after {
  content: "";
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
}

.exchange-overlay-bg::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  background: radial-gradient(
    circle at center,
    rgba(0, 0, 0, 0) 60%,
    rgba(0, 0, 0, 0.6) 100%
  );
  z-index: 3;
}

.black-stroke {
  -webkit-text-stroke-width: 5px;
  -webkit-text-stroke-color: #000;
  text-shadow: 0px 0px 5px rgba(0, 0, 0, 0.95),
    0px 0px 10px rgba(0, 0, 0, 0.85),
    0px 0px 15px rgba(0, 0, 0, 0.75),
    0px 0px 20px rgba(0, 0, 0, 0.65);
  paint-order: stroke fill;
}

.shadow-banner {
  filter: drop-shadow(0px 8px 12px rgba(0, 0, 0, 0.52));
}

.banner-ends-in {
  text-shadow: 0px 0px 115.874px rgba(0, 0, 0, 0.50);
}

.bounce-up {
  animation: backpack-bounce-up 0.6s cubic-bezier(.68, -0.55, .27, 1.55);
}

@keyframes backpack-bounce-up {
  0% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-6px) scale(1.08);
  }
  60% {
    transform: translateY(2px) scale(0.98);
  }
  80% {
    transform: translateY(-4px) scale(1.03);
  }
  100% {
    transform: translateY(0) scale(1);
  }
}
</style>
